import React, { useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "typedefs";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { Loading } from "app/components/Wrappers";
import SessionForm from "../SessionForm";
import SessionParticipantBadge from "../SessionParticipantBadge";
import useBookSessionData from "../hooks/useBookSessionData";

import { selectionForLesson } from "../helpers";
import styles from "../NewSession/styles.module.scss";

import { setBookSession } from "app/actions/scheduling/bookSession";
import { schedulingFetchGet } from "app/utils/request/scheduling/fetch";

import { useTimeZonesModel } from "../indexModel";
import Navbar from "app/components/Navbar";
import { BookSessionState } from "app/reducers/scheduling/bookSession";
import { TimeZoneState } from "app/reducers/scheduling/timeZone";
import { TimeZoneModel } from "app/models/scheduling/TimeZoneModel";

const EditLesson = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const userID = useSelector((state: RootState) => state.user.self.id);
  const bookLessonState = useSelector((state: RootState) => state.bookSession);
  const { t } = useTranslation();

  if (!bookLessonState.selectedSession) {
    history.push("/sessions");
    return null;
  }

  const { coach } = useSelector(
    (state: RootState): BookSessionState => state.bookSession,
  );

  const timeZones = useSelector(
    (state: RootState): TimeZoneState => state?.schedulingTimeZones,
  );

  const timezone =
    timeZones?.data?.find(
      (item: TimeZoneModel) => item?.value === coach?.timeZone,
    )?.display || "";

  const [timeZoneOffset, ...timeZoneName] = timezone?.split(" ");

  const fetcher = useCallback(() => {
    if (!userID) {
      return Promise.resolve([]);
    }
    const facilitiesPromise = schedulingFetchGet("/facilities");
    const coachPromise = schedulingFetchGet("/coach_profiles");

    return Promise.all([facilitiesPromise, coachPromise]);
  }, [userID]);

  const [loadingBookLessonData, bookLessonData] = useBookSessionData(fetcher);

  useEffect(() => {
    if (loadingBookLessonData) {
      return;
    }

    const initialData = selectionForLesson(bookLessonData, bookLessonState);
    dispatch(
      setBookSession({
        ...initialData,
        date: initialData.date.toISODate(),
      }),
    );
    // Following eslint disable is intentional.
    // I do not want this useEffect to rerun if other things change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingBookLessonData, dispatch]);

  const { pending: loadingTimeZones } = useTimeZonesModel();

  if (loadingBookLessonData || !bookLessonState.facility || loadingTimeZones) {
    return <Loading isLoading loadType="spinner" />;
  }

  return (
    <div className={`${styles.container} sessions-page-container`}>
      <Navbar
        label={t("Edit Session")}
        hasSearch={false}
        hasNotifications={false}
        onBackClick={() => history.push("/sessions")}
        className={styles.navbar_wrapper}
      >
        <div className={styles.navbar_right}>
          <SessionParticipantBadge session={bookLessonState.selectedSession} />
          <div className={styles.timezone}>
            <i className={`${styles.tz_ico} ico ico-globe`} />
            <span>{timeZoneOffset}</span>
            <span>{timeZoneName.join(" ")}</span>
          </div>
        </div>
      </Navbar>
      <SessionForm bookLessonData={bookLessonData} editMode disableCoachSelection />
    </div>
  );
};

export default EditLesson;
